import { Controller } from '@hotwired/stimulus'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'

import { firebaseConfig } from '../config'
import { ClassLogger } from '../lib/logging/class_logger'
import { DeviceDetection } from '../lib/device_detection'
import { hideOsSpecificElements, isMaybeIOSSimulator } from '../utilities'
import { showToast } from '../notifications/toast'
import { FirebaseNotificationManager } from '../notifications/firebase_notifications'
import { send_notifications, notification, notifications } from '../routes'

const logger = new ClassLogger('NotificationPermissionController')
const log = logger.log.bind(logger)
const warn = logger.warn.bind(logger)
const logError = logger.error.bind(logger)

const warning = 'Firebase messaging and notifications may not work.'
export default class extends Controller {
  connect () {
    log('[NotificationPermissionController] connect() called')
    if (DeviceDetection.isIOSPWA()) {
      log('[Notifications] iOS PWA detected - using Declarative Web Push instead of Firebase')
      return
    }

    if (window.location.protocol === 'http:') {
      warn(`[Notifications] Service workers must use HTTPS. ${warning}`)
    } else if (navigator.serviceWorker === undefined) {
      warn(`[Notifications] navigator.serviceWorker is undefined. ${warning}`)
    } else if (isMaybeIOSSimulator()) {
      warn(`[Notifications] If you are using iOS Simulator, ${warning}`)
    } else if (Notification === undefined) {
      warn(`[Notifications] Notification is undefined. ${warning}`)
    }

    // Hide instructions for user agents other than the current one.
    hideOsSpecificElements()

    // Replace help text with the curretn domain.
    const domain = window.location.hostname
    this.element.querySelectorAll('.app-domain').forEach(el => el.textContent = domain)

    let supported = true
    if (firebaseConfig.apiKey === '') {
      warn('[Notifications] Firebase API key not found. Firebase is disabled.')
      supported = false
    }
    if (window.Notification === undefined) {
      warn('[Notifications] Notification is undefined. Notifications are disabled.')
      supported = false
    }

    supported ? this.initializeNotifications() : this.onUnsupported()
  }

  async checkForExistingToken (messaging) {
    log('[checkForExistingToken] Starting check for existing Firebase token')
    log(`[checkForExistingToken] VAPID key: ${firebaseConfig.vapidKey}`)

    try {
      // Clean any orphaned browser tokens first, then check state
      console.log('🎯 [Perm] About to sync Firebase token...')
      const ecosystemId = document.querySelector('meta[name="ecosystem_id"]')?.content
      const result = await FirebaseNotificationManager.syncToken(ecosystemId)
      if (result.orphanCleaned) {
        console.log('🧹 [Perm] Orphaned Firebase token cleaned during permission check')
      }
      console.log('🎯 [Perm] Firebase sync complete, checking token...')
      const hasToken = await FirebaseNotificationManager.hasTokenForEcosystem(ecosystemId)
      console.log('🎯 [Perm] Final hasToken result:', hasToken)

      if (!hasToken) {
        log('[checkForExistingToken] No registration token available.')
        window.firebase_token = null
        window.persistedFirebaseToken = null
        this.onSubscriptionStatusChanged()
        return null
      }

      // Get the actual token
      const firebase_token = await getToken(messaging, {
        vapidKey: firebaseConfig.vapidKey,
        serviceWorkerRegistration: FirebaseNotificationManager.serviceWorkerRegistration
      })

      log(`[checkForExistingToken] Got token: ${firebase_token}`)
      window.notificationsPermission = true

      // Check if token is registered on server
      log(`[checkForExistingToken] Checking server for token: ${firebase_token}`)
      const response = await fetch(notification(firebase_token), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      log(`[checkForExistingToken] Server response status: ${response.status}`)

      if (response.status === 200) {
        const body = await response.text()
        log(`[checkForExistingToken] Server returned: ${body}`)
        window.firebase_token = body
        window.persistedFirebaseToken = firebase_token
        this.onSubscriptionStatusChanged()
        return firebase_token
      } else {
        log('[checkForExistingToken] Token not found on server')
        window.firebase_token = null
        window.persistedFirebaseToken = null
        this.onSubscriptionStatusChanged()
        return null
      }
    } catch (err) {
      log('[checkForExistingToken] Error: ', err)
      window.notificationsPermission = false
      if (err.code === 'messaging/permission-blocked') {
        this.onSubscriptionStatusChanged()
      }
      throw err
    }
  };

  createNotification () {
    log('[Firebase] createNotification called')
    const demoForm = document.getElementById('demo-form')
    const formData = new FormData(demoForm)
    const notification = {
      title: formData.get('notification[title]') || '(no title)',
      body: formData.get('notification[body]') || '(no body)'
    }

    // Add navigate field if present
    const navigate = formData.get('notification[navigate]')
    if (navigate && navigate !== '') {
      notification.navigate = navigate
    }

    const payload = {
      notification,
      token_type: 'firebase'
    }
    log('[Firebase] Sending payload:', payload)
    fetch(send_notifications(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: JSON.stringify(payload)
    }).then((response) => {
      log('[Firebase] Response status:', response.status)
      response.json().then((data) => {
        log('[Firebase] Response data:', data)
        showToast('Notification sent', data.message || JSON.stringify(data))
      })
    }).catch((err) => {
      logError('[Firebase] Error:', err)
      showToast('Error', 'An error occurred while sending the notification.')
    })
  };

  fetchAndUpsertNewToken (messaging, _verboseTokenMsg) {
    log('[fetchAndUpsertNewToken] Starting token fetch and registration')
    return new Promise((resolve, reject) => {
      if (Notification.permission === 'denied') {
        warn('[fetchAndUpsertNewToken] Notifications are blocked. Cannot request token.')
        return reject()
      }
      log('[fetchAndUpsertNewToken] Requesting token from Firebase...')
      getToken(messaging, {
        vapidKey: firebaseConfig.vapidKey,
        serviceWorkerRegistration: FirebaseNotificationManager.serviceWorkerRegistration
      }).then((firebase_token) => {
        log(`[fetchAndUpsertNewToken] Received token: ${firebase_token || '(no token)'}`)
        if (!firebase_token) {
          warn('[fetchAndUpsertNewToken] No Firebase registration token available.')
          return
        }
        // Save this token to the server.
        // TODO: move this route to the User resource?
        log('[fetchAndUpsertNewToken] Sending token to server for registration...')
        const ecosystemId = document.querySelector('meta[name="ecosystem_id"]')?.content
        fetch(notifications(), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
          },
          body: JSON.stringify({
            token: firebase_token,
            token_type: 'firebase',
            ecosystem_id: ecosystemId
          })
        })
          .then((response) => {
            log(`[fetchAndUpsertNewToken] Server response status: ${response.status}`)
            response.text().then((body) => {
              log(`[fetchAndUpsertNewToken] Server response: ${body}`)
              showToast(
                'Token registered',
                `${body} You will now receive notifications here.`
              )
              window.firebase_token = firebase_token
              window.persistedFirebaseToken = firebase_token
              this.onSubscriptionStatusChanged()

              resolve(firebase_token)
            })
          })
          .catch(reject)
      }).catch(reject)
    })
  }

  async initializeNotifications () {
    window.firebase_token = null
    window.notificationsPermission = null
    window.persistedFirebaseToken = null

    // Skip Firebase on iOS 18.4+ PWAs - they use Declarative Web Push instead
    if (window.DeclarativeWebPush && window.DeclarativeWebPush.isSupported()) {
      log('[Notifications] Skipping Firebase initialization - using Declarative Web Push')
      return
    }

    // Use shared FirebaseNotificationManager instead of initializing again
    try {
      await FirebaseNotificationManager.initialize()
      const messaging = FirebaseNotificationManager.messaging

      this.subscribeNotificationFormEvents(messaging)

      if (Notification.permission === 'granted') {
        window.notificationsPermission = true
        try {
          await this.checkForExistingToken(messaging)
        } catch (err) {
          log('An error occurred while checking for existing token. ', err)
        }
      } else {
        this.onSubscriptionStatusChanged()
      }
    } catch (e) {
      logError('An error occurred while initializing Firebase messaging. ', e)
      this.onUnsupported()
    }
  }

  onSubscriptionStatusChanged () {
    log('[onSubscriptionStatusChanged] Notification.permission:', Notification.permission)
    log('[onSubscriptionStatusChanged] persistedFirebaseToken:', window.persistedFirebaseToken)
    const blockedEl = document.querySelector('.app-notifications-blocked')
    const defaultEl = document.querySelector('.app-notifications-default')
    const subscribedEl = document.querySelector('.app-notifications-subscribed')
    const unsubscribedEl = document.querySelector('.app-notifications-unsubscribed')
    blockedEl.classList.add('hidden')
    defaultEl.classList.add('hidden')
    subscribedEl.classList.add('hidden')
    unsubscribedEl.classList.add('hidden')
    switch (Notification.permission) {
      case 'denied':
        log('[onSubscriptionStatusChanged] Showing blocked state')
        blockedEl.classList.remove('hidden')
        break
      case 'granted':
        if (window.persistedFirebaseToken !== null) {
          log('[onSubscriptionStatusChanged] Showing subscribed state')
          subscribedEl.classList.remove('hidden')
        } else {
          log('[onSubscriptionStatusChanged] Showing unsubscribed state')
          unsubscribedEl.classList.remove('hidden')
        }
        break
      default:
        log('[onSubscriptionStatusChanged] Showing default state')
        defaultEl.classList.remove('hidden')
        break
    }
  }

  onUnsupported () {
    const show = el => el.classList.remove('hidden')
    const showSelector = selector => this.element.querySelectorAll(selector).forEach(el => show(el))
    showSelector('.app-notifications-unsupported')
  }

  subscribeNotificationFormEvents (messaging) {
    log('[subscribeNotificationFormEvents] Setting up event listeners')
    const loadToken = document.getElementById('app-notifications-load-token')
    loadToken.addEventListener('click', (e) => {
      log('[subscribeNotificationFormEvents] Load token button clicked')
      e.preventDefault()
      this.fetchAndUpsertNewToken(messaging, true).then((firebase_token) => {
        this.onSubscriptionStatusChanged()
      }).finally(_firebase_token => {
        this.onSubscriptionStatusChanged()
      })
    })

    const demoForm = document.getElementById('demo-form')
    demoForm.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return
      e.preventDefault()
      this.createNotification()
    })

    const sendButton = document.getElementById('app-notifications-create')
    sendButton.addEventListener('click', (e) => {
      e.preventDefault()
      this.createNotification()
    })

    const deregister = document.getElementById('app-notifications-deregister')
    deregister.addEventListener('click', (e) => {
      log('[subscribeNotificationFormEvents] Deregister button clicked')
      e.preventDefault()
      if (window.firebase_token === null) {
        log('[subscribeNotificationFormEvents] No token to deregister')
        return showToast('No token', 'You do not have a token registered.')
      }
      log('[subscribeNotificationFormEvents] Deleting token from server:', window.firebase_token)
      fetch(notification(window.firebase_token), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      }).then((response) => {
        log('[subscribeNotificationFormEvents] Server response status:', response.status)
        response.text().then(body => {
          log('[subscribeNotificationFormEvents] Server response:', body)
          FirebaseNotificationManager.deleteTokenFromBrowser().then(() => {
            log('[subscribeNotificationFormEvents] Firebase token deleted from browser')
          }).catch((err) => {
            logError('[subscribeNotificationFormEvents] Error deleting Firebase token:', err)
          })
          window.firebase_token = null
          window.persistedFirebaseToken = null
          showToast('Token deleted', body + ' You will no longer ' +
            'receive notifications here.')
          this.onSubscriptionStatusChanged()
        })
      })
        .catch((err) => {
          logError('[subscribeNotificationFormEvents] Error during deregister:', err)
          showToast('Error', 'An error occurred while deleting the token.')
        })
    })

    const deregisterAll = document.getElementById('app-notifications-deregister-all')
    deregisterAll.addEventListener('click', (e) => {
      log('[subscribeNotificationFormEvents] Deregister all button clicked')
      e.preventDefault()
      log('[subscribeNotificationFormEvents] Deleting all Firebase tokens from server')
      const ecosystemId = document.querySelector('meta[name="ecosystem_id"]')?.content
      fetch(notifications() + `?token_type=firebase&ecosystem_id=${ecosystemId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      }).then((response) => {
        log('[subscribeNotificationFormEvents] Server response status:', response.status)
        response.text().then((body) => {
          log('[subscribeNotificationFormEvents] Server response:', body)
          FirebaseNotificationManager.deleteTokenFromBrowser().then(() => {
            log('[subscribeNotificationFormEvents] Firebase token deleted from browser')
          }).catch((err) => {
            logError('[subscribeNotificationFormEvents] Error deleting Firebase token:', err)
          })
          showToast('All Firebase tokens deleted', body + ' You will no longer ' +
            'receive Firebase notifications.')
          window.persistedFirebaseToken = null
          this.onSubscriptionStatusChanged()
        })
      }).catch((err) => {
        logError('[subscribeNotificationFormEvents] Error during deregister all:', err)
        showToast('Error', 'An error occurred while deleting the tokens.')
      })
    })
  }
}
